'use client';

import { memo, useState } from 'react';
import type { FilterGroup } from '../search/types';
import { useClientConfig } from '@/lib/clients/ClientContext';
import { CloseIcon } from '../search/icons';

function CheckboxIcon({ checked }: { checked: boolean }) {
  if (checked) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 text-brand">
        <rect x="0.5" y="0.5" width="15" height="15" rx="2.5" fill="currentColor" stroke="currentColor" />
        <path d="M12 5L6.5 10.5L4 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
      <rect x="0.5" y="0.5" width="15" height="15" rx="2.5" stroke="#CDCDCD" />
    </svg>
  );
}

interface AgentChatFiltersSidebarProps {
  groups: FilterGroup[];
  selectedItems: string[];
  onFilterToggle: (groupTitle: string, itemName: string) => void;
  /** When set, the header shows a left-chevron to close (slide-over use). */
  onCollapse?: () => void;
}

function AgentChatFiltersSidebar({ groups, selectedItems = [], onFilterToggle, onCollapse }: AgentChatFiltersSidebarProps) {
  const clientConfig = useClientConfig();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleExpand = (title: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  return (
    <div className="flex flex-col items-start w-full h-full overflow-y-auto overscroll-contain custom-scrollbar bg-[#F8F8F8] pt-6 pr-9 pb-6 pl-6 gap-6">
      {/* Title (+ an ✕ to close, in the slide-over) */}
      <div className="flex justify-between items-center self-stretch">
        <h3 className={`font-gotham font-bold text-text-primary leading-[28px] ${onCollapse ? 'text-[16px]' : 'text-[14px]'}`}>
          {clientConfig.ui.strings.filtersTitle}
        </h3>
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Κλείσιμο φίλτρων"
            className="-mr-2 w-9 h-9 flex items-center justify-center text-text-primary hover:text-brand transition-colors cursor-pointer"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Filter groups */}
      <div className="flex flex-col items-start self-stretch">
        {groups.map((group, groupIdx) => {
          const isExpanded = expandedGroups.has(group.title);
          const maxVisible = group.maxVisible;
          const hasMore = maxVisible != null && group.items.length > maxVisible;
          const visibleItems = hasMore && !isExpanded
            ? group.items.slice(0, maxVisible)
            : group.items;
          const hiddenCount = hasMore ? group.items.length - maxVisible : 0;

          return (
            <div
              key={group.title}
              className={`flex flex-col items-start gap-2 self-stretch pb-4 border-b border-[#D8D8D8]${groupIdx > 0 ? ' pt-4' : ''}`}
            >
              <h4 className="text-[13px] font-gotham font-medium text-text-primary leading-[28px] self-stretch">
                {group.title}
              </h4>

              <div className="flex flex-col items-start self-stretch">
                {visibleItems.map(({ name, count, key }) => {
                  const selectionKey = key ?? name;
                  const isSelected = selectedItems.includes(selectionKey);
                  return (
                    <button
                      key={selectionKey}
                      onClick={() => onFilterToggle(group.title, selectionKey)}
                      className="flex h-8 py-2 justify-between items-center self-stretch cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <CheckboxIcon checked={isSelected} />
                        <span className={`text-[13px] font-gotham leading-[14px] truncate ${
                          isSelected ? 'font-medium text-brand' : 'font-normal text-text-primary'
                        }`}>
                          {name}
                        </span>
                      </div>
                      <span className="text-[12px] font-gotham font-normal text-[#6A7282] leading-[14px] flex-shrink-0 ml-2">
                        {count}
                      </span>
                    </button>
                  );
                })}

                {hasMore && (
                  <button
                    onClick={() => toggleExpand(group.title)}
                    className="text-[12px] font-gotham font-medium text-brand mt-1 cursor-pointer hover:underline"
                  >
                    {isExpanded ? clientConfig.ui.strings.showLess : clientConfig.ui.strings.showMore(hiddenCount)}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Memoised: its props only change with the products or the filters, not with
// each update of the streaming reply that re-renders the chat widget.
export default memo(AgentChatFiltersSidebar);
